import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Search, Phone, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLEY = "/brand/product/box-angle.jpg";

export default function StoreLocator() {
  const [query, setQuery] = useState("");
  const [radius, setRadius] = useState(100);
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = async (q = "", r = radius) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      params.set("radius", String(r));
      const res = await fetch(`${API}/retailers?${params.toString()}`);
      const data = await res.json();
      setRetailers(data);
    } catch (e) {
      setRetailers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(query, radius);
  };

  const onRadiusChange = (e) => {
    const r = Number(e.target.value);
    setRadius(r);
    load(query, r);
  };

  return (
    <section id="locator" data-testid="locator" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-12 max-w-2xl"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-blaze mb-4">Store Locator</p>
          <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
            Find it <span className="text-fire">near</span> you
          </h2>
          <p className="text-muted-foreground mt-5 text-base sm:text-lg">
            Enter your ZIP code and pick a radius to find every authorized Hot Packz Grabba retailer near you, or search by city or state.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Search + list */}
          <div className="lg:col-span-2">
            <form onSubmit={onSearch} className="flex flex-col sm:flex-row gap-3 mb-8">
              <div className="flex flex-1 border border-surface-border">
                <span className="flex items-center pl-4 text-muted-foreground">
                  <Search size={18} />
                </span>
                <input
                  data-testid="store-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter ZIP code, city or state…"
                  className="flex-1 bg-black text-white px-4 py-4 text-sm sm:text-base placeholder:text-muted-foreground/60 focus:outline-none"
                />
              </div>
              <div className="flex gap-3">
                <select
                  data-testid="store-radius-select"
                  value={radius}
                  onChange={onRadiusChange}
                  aria-label="Search radius"
                  className="bg-black text-white border border-surface-border px-4 py-4 text-sm sm:text-base font-mono uppercase tracking-wider focus:outline-none focus:border-blaze cursor-pointer"
                >
                  <option value={25}>25 mi</option>
                  <option value={50}>50 mi</option>
                  <option value={100}>100 mi</option>
                  <option value={250}>250 mi</option>
                </select>
                <button
                  data-testid="store-search-btn"
                  type="submit"
                  className="flex-1 sm:flex-none bg-blaze text-white font-heading uppercase text-xs sm:text-sm tracking-wider px-6 sm:px-8 py-4 hover:bg-gold hover:text-ink transition-colors duration-200"
                >
                  Search
                </button>
              </div>
            </form>

            <div data-testid="store-results" className="space-y-px bg-surface-border border border-surface-border max-h-[520px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center gap-3 p-12 bg-ink text-muted-foreground">
                  <Loader2 className="animate-spin" size={18} /> Loading retailers…
                </div>
              ) : retailers.length === 0 ? (
                <div data-testid="store-empty" className="p-12 bg-ink text-center text-muted-foreground">
                  No retailers found for "{query}". Try another city or ZIP, or{" "}
                  <a href="#contact" className="text-blaze underline underline-offset-4">contact us</a>.
                </div>
              ) : (
                retailers.map((r, i) => (
                  <motion.button
                    key={r.id}
                    data-testid={`store-item-${r.id}`}
                    onClick={() => setActive(r.id)}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: Math.min(i * 0.04, 0.3) }}
                    className={`w-full text-left p-6 flex items-start justify-between gap-4 transition-colors duration-200 ${
                      active === r.id ? "bg-surface" : "bg-ink hover:bg-surface"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <MapPin className="shrink-0 mt-1 text-blaze" size={20} />
                      <div>
                        <h3 className="font-heading uppercase text-base tracking-tight">{r.name}</h3>
                        <p className="text-muted-foreground text-sm mt-1">
                          {r.address}, {r.city}, {r.state} {r.zip}
                        </p>
                        {r.phone && (
                          <p className="flex items-center gap-2 text-gold text-sm mt-2 font-mono">
                            <Phone size={13} /> {r.phone}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="font-mono text-xs text-gold/80 shrink-0 text-right">
                      {typeof r.distance_miles === "number" ? `${r.distance_miles} mi` : r.state}
                    </span>
                  </motion.button>
                ))
              )}
            </div>
          </div>

          {/* Abstract map graphic */}
          <div className="relative border border-surface-border overflow-hidden min-h-[300px] lg:min-h-full">
            <img src={ALLEY} alt="Hot Packz Grabba 25-jar grabba case" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-ink/60" />
            <div
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage:
                  "linear-gradient(#222 1px, transparent 1px), linear-gradient(90deg, #222 1px, transparent 1px)",
                backgroundSize: "38px 38px",
              }}
              aria-hidden="true"
            />
            {[
              { top: "22%", left: "30%" },
              { top: "48%", left: "62%" },
              { top: "66%", left: "38%" },
              { top: "34%", left: "74%" },
            ].map((p, i) => (
              <span
                key={i}
                className="absolute"
                style={{ top: p.top, left: p.left }}
              >
                <span className="relative flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-blaze opacity-60 animate-ping" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-blaze" />
                </span>
              </span>
            ))}
            <div className="absolute bottom-5 left-5 right-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Authorized network</p>
              <p className="font-display uppercase text-3xl mt-1 leading-none">
                {retailers.length} <span className="text-outline-gold text-2xl">Locations</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
